import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert,
  TouchableOpacity,
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Chip,
  useTheme,
  Text,
  ActivityIndicator,
  Button,
  TouchableRipple,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import apiService, { Cocktail } from '../services/ApiService';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { ThemedContainer } from '../components/ThemedContainer';
import { navigateToCocktailsWithSearch } from '../utils/navigation';

interface RouteParams {
  cocktailId: number;
}

const CocktailDetailScreen: React.FC = () => {
  const theme = useTheme();
  const { themeData } = useCustomTheme();
  const route = useRoute();
  const navigation = useNavigation();
  const [cocktail, setCocktail] = useState<Cocktail | null>(null);
  const [loading, setLoading] = useState(true);

  const { cocktailId } = route.params as RouteParams;

  useEffect(() => {
    if (cocktailId) {
      loadCocktailDetail();
    }
  }, [cocktailId]);

  const loadCocktailDetail = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCocktail(cocktailId);
      setCocktail(data);
    } catch (error) {
      console.error('Error loading cocktail detail:', error);
      Alert.alert('Error', 'Failed to load cocktail details');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength?: string) => {
    switch (strength) {
      case 'low': return themeData.colors.highlight1;
      case 'standard': return themeData.colors.highlight2;
      case 'strong': return themeData.colors.highlight3;
      default: return themeData.colors.textSecondary;
    }
  };

  const getDifficultyStars = (difficulty?: number) => {
    if (!difficulty) return '';
    return '⭐'.repeat(difficulty) + '☆'.repeat(5 - difficulty);
  };

  const formatPrepTime = (seconds?: number) => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes}m`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatABV = (abv?: number | string) => {
    if (!abv) return '';
    const numAbv = typeof abv === 'string' ? parseFloat(abv) : abv;
    if (isNaN(numAbv)) return '';
    return `${numAbv.toFixed(1)}%`;
  };

  const renderVisualNotes = (visualNotes: any) => {
    if (!visualNotes) return null;
    
    let notes;
    try {
      notes = typeof visualNotes === 'string' ? JSON.parse(visualNotes) : visualNotes;
    } catch (error) {
      console.warn('Failed to parse visual notes:', error);
      return null;
    }
    
    return (
      <View style={styles.notesContent}>
        {notes.clarity && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Clarity</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.clarity}</Text>
          </View>
        )}
        {notes.appearance && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Appearance</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.appearance}</Text>
          </View>
        )}
        {notes.garnish && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Garnish</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.garnish}</Text>
          </View>
        )}
        {notes.glassware && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Glassware</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.glassware}</Text>
          </View>
        )}
        {notes.viscosity && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Viscosity</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.viscosity}</Text>
          </View>
        )}
        {notes.presentation && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight1 }]}>Presentation</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.presentation}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderTastingNotes = (tastingNotes: any) => {
    if (!tastingNotes) return null;
    
    let notes;
    try {
      notes = typeof tastingNotes === 'string' ? JSON.parse(tastingNotes) : tastingNotes;
    } catch (error) {
      console.warn('Failed to parse tasting notes:', error);
      return null;
    }
    
    return (
      <View style={styles.notesContent}>
        {notes.finish && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Finish</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.finish}</Text>
          </View>
        )}
        {notes.balance && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Balance</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.balance}</Text>
          </View>
        )}
        {notes.mouthfeel && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Mouthfeel</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.mouthfeel}</Text>
          </View>
        )}
        {notes.complexity && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Complexity</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.complexity}</Text>
          </View>
        )}
        {notes.primary_tastes && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Primary Tastes</Text>
            <View style={styles.tastesGrid}>
              {Object.entries(notes.primary_tastes).map(([taste, level]) => (
                <View key={taste} style={styles.tasteItem}>
                  <Text style={[styles.tasteLabel, { color: themeData.colors.textSecondary }]}>{taste}</Text>
                  <Text style={[styles.tasteLevel, { color: themeData.colors.text }]}>{String(level)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
        {notes.aromatic_profile && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Aromatic Profile</Text>
            <View style={styles.aromaticTags}>
              {Array.isArray(notes.aromatic_profile) && notes.aromatic_profile.map((aroma: string, index: number) => (
                <View key={index} style={[styles.aromaticTag, { backgroundColor: themeData.colors.highlight2 + '20' }]}>
                  <Text style={[styles.aromaticTagText, { color: themeData.colors.highlight2 }]}>{aroma}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
        {notes.notes && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Notes</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.notes}</Text>
          </View>
        )}
        {notes.flavor_profile && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Flavor Profile</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.flavor_profile}</Text>
          </View>
        )}
        {notes.acidity && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Acidity</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.acidity}</Text>
          </View>
        )}
        {notes.sweetness && (
          <View style={styles.noteItem}>
            <Text style={[styles.noteLabel, { color: themeData.colors.highlight2 }]}>Sweetness</Text>
            <Text style={[styles.noteValue, { color: themeData.colors.text }]}>{notes.sweetness}</Text>
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <ThemedContainer style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={themeData.colors.primary} />
        <Text style={[styles.loadingText, { color: themeData.colors.text }]}>Loading cocktail details...</Text>
      </ThemedContainer>
    );
  }

  if (!cocktail) {
    return (
      <ThemedContainer style={styles.errorContainer}>
        <Text style={[styles.errorText, { color: themeData.colors.text }]}>Cocktail not found</Text>
        <Button 
          mode="contained" 
          onPress={() => navigation.goBack()}
          style={{ backgroundColor: themeData.colors.primary }}
        >
          Go Back
        </Button>
      </ThemedContainer>
    );
  }

  return (
    <ThemedContainer style={styles.mainContainer}>
      <ScrollView style={[styles.scrollView, { backgroundColor: themeData.colors.background }]}>
        {/* Hero Section */}
        <Card style={[styles.heroCard, { backgroundColor: themeData.colors.cardBackground }]}>
          <Card.Content>
            <Title style={[styles.cocktailName, { color: themeData.colors.text }]}>{cocktail.name}</Title>
            
            {cocktail.family_name && (
              <TouchableOpacity 
                onPress={() => navigateToCocktailsWithSearch(navigation, cocktail.family_name!)}
              >
                <Chip 
                  mode="outlined" 
                  style={[styles.familyChip, { backgroundColor: themeData.colors.highlight1 + '20' }]}
                  textStyle={{ color: themeData.colors.highlight1 }}
                >
                  {cocktail.family_name}
                </Chip>
              </TouchableOpacity>
            )}

            {cocktail.instructions && (
              <Paragraph style={[styles.description, { color: themeData.colors.textSecondary }]}>
                {cocktail.instructions}
              </Paragraph>
            )}

            {/* Quick Stats */}
            <View style={styles.quickStats}>
              {cocktail.method_name && (
                <TouchableOpacity 
                  style={styles.statItem}
                  onPress={() => navigateToCocktailsWithSearch(navigation, cocktail.method_name!)}
                >
                  <Ionicons name="flask" size={16} color={themeData.colors.highlight2} />
                  <Text style={[styles.statText, { color: themeData.colors.textSecondary }]}>{cocktail.method_name}</Text>
                </TouchableOpacity>
              )}
              {cocktail.glassware_name && (
                <TouchableOpacity 
                  style={styles.statItem}
                  onPress={() => navigateToCocktailsWithSearch(navigation, cocktail.glassware_name!)}
                >
                  <Ionicons name="wine" size={16} color={themeData.colors.highlight3} />
                  <Text style={[styles.statText, { color: themeData.colors.textSecondary }]}>{cocktail.glassware_name}</Text>
                </TouchableOpacity>
              )}
              {cocktail.difficulty && (
                <View style={styles.statItem}>
                  <Ionicons name="star" size={16} color={themeData.colors.highlight4} />
                  <Text style={[styles.statText, { color: themeData.colors.textSecondary }]}>
                    {getDifficultyStars(cocktail.difficulty)}
                  </Text>
                </View>
              )}
              {cocktail.prep_time_seconds && (
                <View style={styles.statItem}>
                  <Ionicons name="time" size={16} color={themeData.colors.highlight1} />
                  <Text style={[styles.statText, { color: themeData.colors.textSecondary }]}>
                    {formatPrepTime(cocktail.prep_time_seconds)}
                  </Text>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>

        {/* Ingredients Section */}
        {(() => {
          let ingredients = [];
          try {
            if (typeof cocktail.ingredients === 'string') {
              ingredients = JSON.parse(cocktail.ingredients);
            } else if (Array.isArray(cocktail.ingredients)) {
              ingredients = cocktail.ingredients;
            }
          } catch (e) {
            console.warn('Failed to parse ingredients:', e);
          }
          
          return ingredients && ingredients.length > 0 ? (
            <Card style={[styles.sectionCard, { backgroundColor: themeData.colors.cardBackgroundVariant }]}>
              <Card.Content>
                <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>🥃 Ingredients</Title>
                {ingredients.map((ingredient: any, index: number) => (
                  <TouchableOpacity 
                    key={index} 
                    style={styles.ingredientItem}
                    onPress={() => navigateToCocktailsWithSearch(navigation, ingredient.name)}
                  >
                    <Text style={[styles.ingredientName, { color: themeData.colors.text }]}>
                      {ingredient.name}
                    </Text>
                    {(ingredient.quantity || ingredient.unit) && (
                      <Text style={[styles.ingredientAmount, { color: themeData.colors.textSecondary }]}>
                        {ingredient.quantity} {ingredient.unit}
                      </Text>
                    )}
                    {ingredient.prep_note && (
                      <Text style={[styles.ingredientPrep, { color: themeData.colors.highlight2 }]}>
                        {ingredient.prep_note}
                      </Text>
                    )}
                    {ingredient.optional && (
                      <Text style={[styles.ingredientPrep, { color: themeData.colors.highlight3 }]}>
                        (Optional)
                      </Text>
                    )}
                  </TouchableOpacity>
                ))}
              </Card.Content>
            </Card>
          ) : null;
        })()}

        

        {/* Additional Info */}
        <Card style={[styles.sectionCard, { backgroundColor: themeData.colors.cardBackground }]}>
          <Card.Content>
            <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>
              <Ionicons name="information-circle" size={20} color={themeData.colors.primary} style={{ marginRight: 8 }} />
              Additional Info
            </Title>
            
            <View style={styles.additionalInfoGrid}>
              {cocktail.iba_status && cocktail.iba_status !== 'Unspecified' && (
                <View style={[styles.infoCard, { backgroundColor: themeData.colors.highlight1 + '15' }]}>
                  <View style={[styles.infoIconContainer, { backgroundColor: themeData.colors.highlight1 + '30' }]}>
                    <Ionicons name="trophy" size={16} color={themeData.colors.highlight1} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: themeData.colors.highlight1 }]}>IBA Status</Text>
                    <Text style={[styles.infoValue, { color: themeData.colors.text }]}>{cocktail.iba_status}</Text>
                  </View>
                </View>
              )}

              {cocktail.strength_band && (
                <View style={[styles.infoCard, { backgroundColor: themeData.colors.highlight2 + '15' }]}>
                  <View style={[styles.infoIconContainer, { backgroundColor: themeData.colors.highlight2 + '30' }]}>
                    <Ionicons name="flame" size={16} color={themeData.colors.highlight2} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: themeData.colors.highlight2 }]}>Strength</Text>
                    <Text style={[styles.infoValue, { color: themeData.colors.text }]}>{cocktail.strength_band}</Text>
                  </View>
                </View>
              )}

              {cocktail.abv_serve && (
                <View style={[styles.infoCard, { backgroundColor: themeData.colors.highlight3 + '15' }]}>
                  <View style={[styles.infoIconContainer, { backgroundColor: themeData.colors.highlight3 + '30' }]}>
                    <Ionicons name="analytics" size={16} color={themeData.colors.highlight3} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: themeData.colors.highlight3 }]}>ABV</Text>
                    <Text style={[styles.infoValue, { color: themeData.colors.text }]}>{formatABV(cocktail.abv_serve)}</Text>
                  </View>
                </View>
              )}

              {cocktail.rating_avg && (
                <View style={[styles.infoCard, { backgroundColor: themeData.colors.highlight4 + '15' }]}>
                  <View style={[styles.infoIconContainer, { backgroundColor: themeData.colors.highlight4 + '30' }]}>
                    <Ionicons name="star" size={16} color={themeData.colors.highlight4} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: themeData.colors.highlight4 }]}>Rating</Text>
                    <Text style={[styles.infoValue, { color: themeData.colors.text }]}>
                      {cocktail.rating_avg.toFixed(1)}/5
                    </Text>
                    <Text style={[styles.infoSubtext, { color: themeData.colors.textSecondary }]}>
                      ({cocktail.rating_count || 0} reviews)
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Notes Section */}
            {(cocktail.visual_notes || cocktail.tasting_notes) && (
              <View style={styles.notesSection}>
                {cocktail.visual_notes && (
                  <View style={[styles.noteCard, { backgroundColor: themeData.colors.highlight1 + '10' }]}>
                    <View style={styles.noteHeader}>
                      <View style={[styles.noteIconContainer, { backgroundColor: themeData.colors.highlight1 + '20' }]}>
                        <Ionicons name="eye" size={18} color={themeData.colors.highlight1} />
                      </View>
                      <Text style={[styles.noteTitle, { color: themeData.colors.highlight1 }]}>Visual Notes</Text>
                    </View>
                    {renderVisualNotes(cocktail.visual_notes)}
                  </View>
                )}

                {cocktail.tasting_notes && (
                  <View style={[styles.noteCard, { backgroundColor: themeData.colors.highlight2 + '10' }]}>
                    <View style={styles.noteHeader}>
                      <View style={[styles.noteIconContainer, { backgroundColor: themeData.colors.highlight2 + '20' }]}>
                        <Ionicons name="restaurant" size={18} color={themeData.colors.highlight2} />
                      </View>
                      <Text style={[styles.noteTitle, { color: themeData.colors.highlight2 }]}>Tasting Notes</Text>
                    </View>
                    {renderTastingNotes(cocktail.tasting_notes)}
                  </View>
                )}
              </View>
            )}
          </Card.Content>
        </Card>
      </ScrollView>
     </ThemedContainer>
   );
 };

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 16,
    paddingBottom: 32,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 18,
    marginBottom: 16,
  },
  heroCard: {
    marginBottom: 16,
    elevation: 4,
    borderRadius: 12,
    overflow: 'hidden',
  },
  cocktailName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  familyChip: {
    alignSelf: 'flex-start',
    marginBottom: 16,
    borderRadius: 20,
  },
  description: {
    fontSize: 16,
    lineHeight: 22,
  },
  quickStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  statItem: {
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  statText: {
    fontSize: 12,
    marginTop: 4,
  },
  sectionCard: {
    marginBottom: 16,
    elevation: 2,
    borderRadius: 12,
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  ingredientItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: 'rgba(0,0,0,0.1)',
  },
  ingredientName: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  ingredientAmount: {
    fontSize: 14,
    fontWeight: '500',
  },
  ingredientPrep: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 4,
  },

  infoItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
   
  additionalInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  infoCard: {
    width: '48%', // Two columns
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  infoIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  infoContent: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  infoSubtext: {
    fontSize: 12,
    marginTop: 4,
  },
  notesSection: {
    marginTop: 16,
  },
  noteCard: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  noteTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  noteText: {
    fontSize: 14,
    lineHeight: 20,
  },
  noteIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  notesContent: {
    marginTop: 12,
  },
  noteItem: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  noteLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'capitalize',
  },
  noteValue: {
    fontSize: 15,
    lineHeight: 20,
  },
  tastesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  tasteItem: {
    width: '50%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 6,
    marginBottom: 4,
    marginRight: 4,
  },
  tasteLabel: {
    fontSize: 13,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  tasteLevel: {
    fontSize: 13,
    fontWeight: '600',
  },
  aromaticTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  aromaticTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 6,
  },
  aromaticTagText: {
    fontSize: 13,
    fontWeight: '500',
  },
 });

export default CocktailDetailScreen;
